import React, {useEffect, useState} from 'react'
import userService from '../../services/user.service';
import Loading from "../common/Loading";
import {useEyeconxStore} from "../../store/eyeconxStore";
import {useNavigate, useParams} from "react-router-dom";
import Swal from '../helpers/swal';
const Profile = () => {
    const user = useEyeconxStore((state) => state.user);
    const clinicId = useEyeconxStore((state) => state.clinicId);
    const logout = useEyeconxStore((state) => state.logout);
    const navigate = useNavigate();
    const {id} = useParams();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Change Password States
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState({});

    useEffect(() => {
        fetchUserProfile();
    }, [clinicId, user?.ID]);

    const fetchUserProfile = async () => {
        try {
            setLoading(true);
            setError('');
            const userId = id || user?.ID;
            if (clinicId && userId) {
                const response = await userService.getUser(clinicId, userId);
                setProfile(response.data);
                setFormData(response.data);
            }
        } catch (err) {
            setError('Failed to fetch user profile');
            console.error('Error fetching profile:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Reset form data when canceling edit
            setFormData(profile);
            setError('');
            setSuccessMessage('');
        }
        setIsEditing(!isEditing);
    };

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccessMessage('');

            const userId = id || user?.ID;
            if (!clinicId || !userId) {
                setError('Missing clinic ID or user ID');
                return;
            }

            // Prepare the data for update (only include fields that can be updated)
            const updateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                username: formData.username,
                email: formData.email,
                isActive: formData.isActive,
                isAuthenticated: formData.isAuthenticated,
                role: formData.role
            };

            // Call the update API
            const response = await userService.updateUser(userId, clinicId, updateData);

            // Update local state with the response data
            setProfile(response.data);
            setFormData(response.data);
            setIsEditing(false);

            // Show success alert
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Profile updated successfully!',
                timer: 3000,
                showConfirmButton: false
            });

        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to update profile';
            setError(errorMessage);

            // Show error alert
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
                timer: 4000,
                showConfirmButton: true
            });
        } finally {
            setSaving(false);
        }
    };

    // Change Password Functions
    const handleChangePasswordToggle = () => {
        setShowChangePassword(!showChangePassword);
        setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordErrors({});
        setError('');
        setSuccessMessage('');
    };

    const handlePasswordInputChange = (e) => {
        const {name, value} = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear specific error when user starts typing
        if (passwordErrors[name]) {
            setPasswordErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validatePasswordForm = () => {
        const errors = {};

        if (!passwordData.oldPassword) {
            errors.oldPassword = 'Old password is required';
        }

        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 10) {
            errors.newPassword = 'Password must be at least 10 characters long';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'New password and confirm password do not match';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChangePassword = async () => {
        if (!validatePasswordForm()) {
            return;
        }

        try {
            setChangingPassword(true);
            setError('');
            setSuccessMessage('');

            const userId = id || user?.ID;
            if (!clinicId || !userId) {
                setError('Missing clinic ID or user ID');
                return;
            }

            // Prepare password change data
            const passwordChangeData = {
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword
            };

            // Call the change password API
            await userService.changePassword(userId, clinicId, passwordChangeData);

            // Reset form
            setShowChangePassword(false);
            setPasswordData({
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

            // Show success alert with countdown
            Swal.fire({
                icon: 'success',
                title: 'Password Changed!',
                html: 'Password changed successfully!<br>You will be logged out in <strong id="countdown">5</strong> seconds for security reasons.',
                timer: 5000,
                timerProgressBar: true,
                showConfirmButton: false,
                didOpen: () => {
                    const timer = Swal.getTimerLeft();
                    const countdownEl = document.getElementById('countdown');
                    if (countdownEl) {
                        const interval = setInterval(() => {
                            const timeLeft = Swal.getTimerLeft();
                            if (countdownEl) {
                                countdownEl.textContent = Math.ceil(timeLeft / 1000);
                            }
                            if (timeLeft <= 0) {
                                clearInterval(interval);
                            }
                        }, 100);
                    }
                }
            }).then(() => {
                handleLogout();
            });

        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to change password';
            setError(errorMessage);

            // Show error alert
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
                timer: 4000,
                showConfirmButton: true
            });
        } finally {
            setChangingPassword(false);
        }
    };

    const getRoleBadgeVariant = (role) => {
        switch (role) {
            case 'ADMIN':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    if (loading) return <Loading/>;

    return (
        <div className="container mt-4">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    {/* Success Message */}
                    {successMessage && (
                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                            {successMessage}
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setSuccessMessage('')}
                            ></button>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {error}
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setError('')}
                            ></button>
                        </div>
                    )}

                    <div className="card shadow-sm">
                        <div className="card-header bg-white py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="card-title">User Profile</div>
                                <div className="d-flex gap-2">
                                    <button
                                        onClick={handleChangePasswordToggle}
                                        className="btn btn-sm btn-warning"
                                        disabled={saving || changingPassword}
                                    >
                                        <i className="bi bi-key me-1"></i>
                                        Change Password
                                    </button>
                                    <button
                                        onClick={handleEditToggle}
                                        className={`btn btn-sm ${isEditing ? 'btn-danger' : 'btn-outline-primary'}`}
                                        disabled={saving || changingPassword}
                                    >
                                        {isEditing ? (
                                            <>
                                                <i className="bi bi-x me-1"></i>
                                                Cancel
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-pencil me-1"></i>
                                                Edit Profile
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="card-body p-4">
                            {/* Change Password Form */}
                            {showChangePassword && (
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="card card-padding card-border shadow-none">
                                            <div className="card-title">
                                                <div className="mb-0">
                                                    <i className="bi bi-key me-2"></i>
                                                    Change Password
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label fw-semibold">Old Password</label>
                                                        <input
                                                            type="password"
                                                            name="oldPassword"
                                                            className={`form-control ${passwordErrors.oldPassword ? 'is-invalid' : ''}`}
                                                            value={passwordData.oldPassword}
                                                            onChange={handlePasswordInputChange}
                                                            disabled={changingPassword}
                                                            placeholder="Enter old password"
                                                        />
                                                        {passwordErrors.oldPassword && (
                                                            <div className="invalid-feedback">
                                                                {passwordErrors.oldPassword}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label fw-semibold">New Password</label>
                                                        <input
                                                            type="password"
                                                            name="newPassword"
                                                            className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                                                            value={passwordData.newPassword}
                                                            onChange={handlePasswordInputChange}
                                                            disabled={changingPassword}
                                                            placeholder="At least 10 characters"
                                                        />
                                                        {passwordErrors.newPassword && (
                                                            <div className="invalid-feedback">
                                                                {passwordErrors.newPassword}
                                                            </div>
                                                        )}
                                                        <div className="form-text">
                                                            Password must be at least 10 characters long
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label fw-semibold">Confirm
                                                            Password</label>
                                                        <input
                                                            type="password"
                                                            name="confirmPassword"
                                                            className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                                                            value={passwordData.confirmPassword}
                                                            onChange={handlePasswordInputChange}
                                                            disabled={changingPassword}
                                                            placeholder="Confirm new password"
                                                        />
                                                        {passwordErrors.confirmPassword && (
                                                            <div className="invalid-feedback">
                                                                {passwordErrors.confirmPassword}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <button
                                                        onClick={handleChangePasswordToggle}
                                                        className="btn btn-sm btn-danger"
                                                        disabled={changingPassword}
                                                    >
                                                        <i className="bi bi-x"></i> Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleChangePassword}
                                                        disabled={changingPassword}
                                                        className="btn btn-sm btn-success"
                                                    >
                                                        {changingPassword ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2"
                                                                      role="status"></span>
                                                                Changing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-check"></i> Change Password
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Profile Header */}
                            <div className="row mb-4 pb-3 border-bottom">
                                <div className="col-auto">
                                    <div
                                        className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                        style={{width: '80px', height: '80px', fontSize: '1.5rem'}}
                                    >
                                        {profile?.firstName?.charAt(0)}{profile?.lastName?.charAt(0)}
                                    </div>
                                </div>
                                <div className="col">
                                    <h3 className="mb-1">{profile?.firstName} {profile?.lastName}</h3>
                                    <span className={`badge bg-${getRoleBadgeVariant(profile?.role)}`}>
                                        {profile?.role}
                                    </span>
                                    <p className="text-muted mb-0 mt-1">{profile?.email}</p>
                                </div>
                            </div>

                            {!profile ? (
                                <div className="alert alert-warning text-center">
                                    No profile data found
                                </div>
                            ) : (
                                <>
                                    {/* Profile Form */}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">User ID</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={profile.id || ''}
                                                disabled
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">User Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={profile.number || ''}
                                                disabled
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">First Name</label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                className="form-control"
                                                value={formData.firstName || ''}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Last Name</label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                className="form-control"
                                                value={formData.lastName || ''}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Username</label>
                                            <input
                                                type="text"
                                                name="username"
                                                className="form-control"
                                                value={formData.username || ''}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                value={formData.email || ''}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                            />
                                        </div>
                                    </div>

                                    {/* Account Status */}
                                    <div className="row mt-4">
                                        <div className="col-12">
                                            <h5 className="mb-3">Account Status</h5>
                                            <div className="row">
                                                <div className="col-md-4 mb-2">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="isActive"
                                                            checked={formData.isActive || false}
                                                            onChange={handleInputChange}
                                                            disabled={!isEditing}
                                                            id="isActive"
                                                        />
                                                        <label className="form-check-label fw-semibold"
                                                               htmlFor="isActive">
                                                            Active
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 mb-2">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="isAuthenticated"
                                                            checked={formData.isAuthenticated || false}
                                                            onChange={handleInputChange}
                                                            disabled={!isEditing}
                                                            id="isAuthenticated"
                                                        />
                                                        <label className="form-check-label fw-semibold"
                                                               htmlFor="isAuthenticated">
                                                            Authenticated
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Timestamps */}
                                    <div className="row mt-4">
                                        <div className="col-md-6 mb-2">
                                            <div className="card bg-light">
                                                <div className="card-body py-2">
                                                    <small className="text-muted">Created At</small>
                                                    <div className="fw-semibold">{profile.createdAt}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="card bg-light">
                                                <div className="card-body py-2">
                                                    <small className="text-muted">Last Modified</small>
                                                    <div className="fw-semibold">{profile.modifiedAt}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    {isEditing && (
                                        <div className="row mt-4">
                                            <div className="col-12">
                                                <div className="d-flex justify-content-end">
                                                    <button
                                                        onClick={handleSave}
                                                        disabled={saving}
                                                        className="btn btn-sm btn-success"
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2"
                                                                      role="status"></span>
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-floppy me-2"></i>
                                                                Save
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;